import { useMemo } from 'react';
import type { IDocumentAnnotation } from '@apis/documentAnnotations';
import type { IPaintedItem } from '../AnnotationsBody/AnnotationCanvas/AnnotationCanvas';
import type { MarkupItem } from '../utils/markupTypes';

/** Every visible mark, by the page it belongs to — what the flattened download
 *  writes into the file. */
export function useVisibleItemsByPage(
  visible: readonly IDocumentAnnotation[],
): Record<number, MarkupItem[]> {
  return useMemo(() => {
    const byPage: Record<number, MarkupItem[]> = {};
    for (const annotation of visible) {
      for (const item of annotation.items) (byPage[item.page] ??= []).push(item);
    }
    return byPage;
  }, [visible]);
}

/**
 * What the canvas paints: the marks of the checked annotations on the page on
 * screen, each still knowing which annotation it came from — that link is the
 * whole of attribution, since an item belongs to exactly one.
 */
export function usePaintedMarks(
  visible: readonly IDocumentAnnotation[],
  page: number,
): IPaintedItem[] {
  return useMemo(
    () =>
      visible.flatMap((annotation) =>
        annotation.items
          .filter((item) => item.page === page)
          .map((item) => ({ annotationId: annotation.id, item })),
      ),
    [visible, page],
  );
}
